import {
  fetchClientsStart,
  fetchClientsSuccess,
  fetchClientsFailure,
  fetchClientAppsStart,
  fetchClientAppsSuccess,
  fetchClientAppsFailure,
  fetchClientScriptsStart,
  fetchClientScriptsSuccess,
  fetchClientScriptsFailure,
  fetchClientStatsStart,
  fetchClientStatsSuccess,
  fetchClientStatsFailure,
  testConnectionStart,
  testConnectionSuccess,
  testConnectionFailure,
} from '../slices/clientsSlice';
import clientsService from '../../services/api/clientsService';
import appRegistryService from '../../services/api/appRegistryService';
import extensionScriptsService from '../../services/api/extensionScriptsService';

export const fetchClients = () => async (dispatch) => {
  try {
    dispatch(fetchClientsStart());
    const response = await clientsService.getClients();
    dispatch(fetchClientsSuccess(response.data));
  } catch (error) {
    console.error('Error fetching clients:', error);
    dispatch(fetchClientsFailure(error.message || 'Failed to fetch clients'));
  }
};

// [9a-endpoint-fix] Switched from /clients/:id/apps (broken Azure-privatelink
// Postgres path via ClientConnectionService) to /app-registry/matrix/by-client/:tag
// which uses the local app-registry tables (client_app_configs).
export const fetchClientApps = (clientIdOrTag) => async (dispatch, getState) => {
  try {
    dispatch(fetchClientAppsStart());

    // Resolve the client tag. Callers usually pass the numeric id; look it up
    // in state. Accept a tag directly if that's what was passed.
    const state = getState();
    const selected = state.clients?.selectedClient;
    const list = state.clients?.clients || [];
    let clientTag = null;
    if (selected && (String(selected.id) === String(clientIdOrTag) || selected.tag === clientIdOrTag)) {
      clientTag = selected.tag;
    } else {
      const found = list.find(
        (c) => String(c.id) === String(clientIdOrTag) || c.tag === clientIdOrTag
      );
      clientTag = found?.tag || (typeof clientIdOrTag === 'string' ? clientIdOrTag : null);
    }

    if (!clientTag) {
      dispatch(fetchClientAppsFailure('Unable to resolve client tag'));
      return;
    }

    const response = await appRegistryService.getMatrixByClientTag(clientTag);
    const rows = Array.isArray(response.data) ? response.data : [];

    // Row shape: { app: AppDefinition, config: ClientAppConfig | null }
    // UI shows only apps actually enabled for this client.
    const apps = rows
      .filter((r) => r?.config?.isEnabled === true)
      .map((r) => {
        const app = r.app || {};
        const cfg = r.config || {};
        return {
          id: app.id,
          name: app.name,
          displayName: app.displayName || app.name,
          configKey: app.configKey,
          logo: app.logo || app.icon || app.metadata?.logo || null,
          ssoType: app.ssoType || cfg.ssoType || null,
          isActive: app.isActive !== false,
          status: app.isActive === false ? 'inactive' : 'active',
          lastSyncAt: cfg.lastSyncAt || null,
          lastSyncStatus: cfg.lastSyncStatus || null,
        };
      });

    dispatch(fetchClientAppsSuccess(apps));
  } catch (error) {
    console.error(`Error fetching apps for client ${clientIdOrTag}:`, error);
    const msg =
      error.response?.data?.message ||
      error.message ||
      'Failed to load apps for this client';
    dispatch(fetchClientAppsFailure(msg));
  }
};

// Derives scripts for a client by:
//   1. Loading the enabled-apps matrix for the client's tag (app-registry)
//   2. Filtering the global extension-scripts list to those whose appKey
//      matches a configKey that's enabled for this client.
// This avoids the legacy /clients/:id/scripts endpoint, which queries the
// separate IntegrationScript table (unused) and always returned 0.
export const fetchClientScripts = (clientIdOrTag) => async (dispatch, getState) => {
  try {
    dispatch(fetchClientScriptsStart());

    // Resolve the client tag the same way fetchClientApps does.
    const state = getState();
    const selected = state.clients?.selectedClient;
    const list = state.clients?.clients || [];
    let clientTag = null;
    if (selected && (String(selected.id) === String(clientIdOrTag) || selected.tag === clientIdOrTag)) {
      clientTag = selected.tag;
    } else {
      const found = list.find(
        (c) => String(c.id) === String(clientIdOrTag) || c.tag === clientIdOrTag
      );
      clientTag = found?.tag || (typeof clientIdOrTag === 'string' ? clientIdOrTag : null);
    }

    if (!clientTag) {
      dispatch(fetchClientScriptsFailure('Unable to resolve client tag'));
      return;
    }

    // Parallel fetch: client app matrix + all extension-scripts
    const [matrixRes, scriptsRes] = await Promise.all([
      appRegistryService.getMatrixByClientTag(clientTag),
      extensionScriptsService.getAll(),
    ]);

    const enabledConfigKeys = new Set(
      (Array.isArray(matrixRes.data) ? matrixRes.data : [])
        .filter((r) => r?.config?.isEnabled === true)
        .map((r) => r.app?.configKey)
        .filter(Boolean)
    );

    const allScripts = Array.isArray(scriptsRes.data) ? scriptsRes.data : [];
    const clientScripts = allScripts
      .filter((s) => s.isActive !== false && enabledConfigKeys.has(s.appKey))
      .map((s) => ({
        id: s.id,
        name: s.name,
        appKey: s.appKey,
        appName: s.appKey, // configKey fallback; UI will replace via lookup
        configKey: s.appKey,
        type: s.scriptType || s.type,
        scriptType: s.scriptType || s.type,
        version: s.version,
        lastRunStatus: s.lastRunStatus || null,
        lastRunAt: s.lastRunAt || null,
        updatedAt: s.updatedAt,
      }));

    dispatch(fetchClientScriptsSuccess(clientScripts));
  } catch (error) {
    console.error(`Error fetching scripts for client ${clientIdOrTag}:`, error);
    dispatch(fetchClientScriptsFailure('Failed to load scripts'));
  }
};

export const fetchClientStats = (clientId) => async (dispatch) => {
  try {
    dispatch(fetchClientStatsStart());
    const response = await clientsService.getClientStats(clientId);
    dispatch(fetchClientStatsSuccess(response.data));
  } catch (error) {
    console.error(`Error fetching stats for client ${clientId}:`, error);
    dispatch(fetchClientStatsFailure(error.message));
  }
};

export const testClientConnection = (clientId) => async (dispatch) => {
  try {
    dispatch(testConnectionStart());
    const response = await clientsService.testClientConnection(clientId);
    dispatch(testConnectionSuccess(response.data));
  } catch (error) {
    console.error(`Error testing connection for client ${clientId}:`, error);
    dispatch(testConnectionFailure('Connection unavailable'));
  }
};
